import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTodayAttendance } from "@/api/hooks/attendance";
import { LogoutAttendancePrompt } from "@/modules/attendance/components/LogoutAttendancePrompt";
import {
  Home,
  Users,
  // Layers,
  // FolderOpen,
  // ClipboardCheck,
  Clock,
  CalendarDays,
  Building2,
  // Building,
  // LayoutTemplate,
  ChevronsLeft,
  ChevronsRight,
  // FileText,
  MapPin,
  Bell,
  // Settings2,
  // AlertTriangle,
  // Activity,
  BarChart3,
  LogOut,
  Smartphone,
} from "@/shared/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useLogout } from "@/api/hooks/auth";
import { useAuth, useAuthStore } from "@/modules/auth";
import { useUIStore } from "@/shared/store/ui";
import { cn } from "@/shared/utils/cn";
import { useApprovals } from "@/api/hooks/approvals";
import { useLeaves } from "@/api/hooks/leaves";
import { useUnreadCount } from "@/api/hooks/notifications";
import { usePendingWorkUnitsCount } from "@/api/hooks/timetracking";
import { useRegularisations } from "@/api/hooks/regularisations";
import { useDeactivationRequests, useWorkUnitRequests } from "@/api/hooks/workunits";
import { useClientDocsCounts } from "@/api/hooks/clientportal";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  /** Single key OR a list (any-of). If set, the actor must have at least one. */
  permission?: string | string[];
  /** If set, item is hidden for actors whose role is in this list. */
  hideForRoles?: string[];
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    group: "Main",
    items: [
      // { to: "/dashboard", label: "Dashboard", icon: Home, hideForRoles: ["client"] },
      // {
      //   to: "/portal",
      //   label: "My Documents",
      //   icon: FileText,
      //   permission: "clientdocs.upload",
      //   hideForRoles: ["technical_admin", "super_admin", "operations", "hr", "admin", "employee"],
      // },
      // { to: "/live-monitor", label: "Live Monitor", icon: Activity, permission: "liveMonitor.view" },
      // { to: "/work-units", label: "Work Units", icon: Layers, permission: "workunits.read", hideForRoles: ["client"] },
      // { to: "/projects", label: "Projects", icon: FolderOpen, permission: "projects.read" },
      // { to: "/approvals", label: "Workunit Approvals", icon: ClipboardCheck, permission: "approvals.resolve", hideForRoles: ["hr", "client"] },
      { to: "/attendance", label: "Attendance", icon: Clock, permission: "attendance.read_self", hideForRoles: ["client"] },
      { to: "/leaves", label: "Leaves", icon: CalendarDays, permission: ["leaves.read_self", "leaves.read_all"] },
      {
        to: "/regularisations",
        label: "Regularisations",
        icon: Clock,
        hideForRoles: ["client"],
      },
      { to: "/notifications", label: "Notifications", icon: Bell, permission: "notifications.read_self", hideForRoles: ["client"] },
    ],
  },
  {
    group: "Admin",
    items: [
      { to: "/users", label: "Users", icon: Users, permission: "users.read" },
      { to: "/phone-requests", label: "Phone Requests", icon: Smartphone, permission: "devices.approve_change" },
      { to: "/departments", label: "Departments", icon: Building2, permission: "departments.read" },
      // { to: "/clients", label: "Clients", icon: Building, permission: "clients.read", hideForRoles: ["employee", "client"] },
      // { to: "/templates", label: "Templates", icon: LayoutTemplate, permission: "templates.read" },
      { to: "/locations", label: "Locations", icon: MapPin, permission: "locations.read" },
      { to: "/holidays", label: "Holidays", icon: CalendarDays, permission: "holidays.read", hideForRoles: ["employee", "client"] },
      // { to: "/documents", label: "Documents", icon: FileText, permission: "documents.registry.read" },
      // { to: "/client-docs", label: "Client Documents", icon: FileText, permission: "clientdocs.list.global" },
      { to: "/reports", label: "Reports", icon: BarChart3, permission: "reports.view" },
      // {
      //   to: "/messaging",
      //   label: "Messaging Setup",
      //   icon: Settings2,
      //   permission: "messaging.manage",
      //   hideForRoles: ["super_admin", "operations", "hr", "admin", "employee", "client"],
      // },
      // {
      //   to: "/error-log",
      //   label: "Error Log",
      //   icon: AlertTriangle,
      //   permission: "errorLog.view",
      //   hideForRoles: ["super_admin", "operations", "hr", "admin", "employee", "client"],
      // },
    ],
  },
];

const ROLE_LABEL: Record<string, string> = {
  technical_admin: "TECHNICAL ADMIN",
  super_admin: "SUPER ADMIN",
  operations: "OPERATIONS",
  hr: "HR",
  admin: "ADMIN",
  employee: "EMPLOYEE",
  client: "CLIENT",
};

const ROLE_STRIP_CLASS: Record<string, string> = {
  technical_admin: "side-strip-superadmin",
  super_admin: "side-strip-superadmin",
  operations: "side-strip-operations",
  hr: "side-strip-operations",
  admin: "side-strip-admin",
  employee: "side-strip-employee",
  client: "side-strip-client",
};

export function Sidebar() {
  const { user } = useAuth();
  const permissions = useAuthStore((s) => s.permissions);
  const forceLogout = useAuthStore((s) => s.forceLogout);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const closeMobileSidebar = useUIStore((s) => s.closeMobileSidebar);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const logout = useLogout();

  const role = user?.role ?? "employee";
  // Attendance-bearing roles see the mark-out prompt before sign-out.
  const attendanceRoleEligible =
    role === "admin" || role === "operations" || role === "employee" || role === "hr";
  const todayAtt = useTodayAttendance(attendanceRoleEligible);
  const hasOpenAttendance =
    !!todayAtt.data?.attendance && !todayAtt.data.attendance.markedOutAt;
  const [logoutPromptOpen, setLogoutPromptOpen] = useState(false);

  const finalizeLogout = async () => {
    try {
      await logout.mutateAsync();
    } catch {
      // server clears refresh cookie regardless
    }
    qc.clear();
    forceLogout("user");
    navigate("/login", { replace: true });
  };

  const onSignOut = () => {
    if (hasOpenAttendance) {
      setLogoutPromptOpen(true);
      return;
    }
    void finalizeLogout();
  };

  const stripClass = ROLE_STRIP_CLASS[role] ?? "side-strip-employee";

  // Pending count badges (rendered next to nav labels). Each query is gated
  // by permission so disabled hooks don't fire for users without access.
  const canResolveApprovals = permissions.has("approvals.resolve");
  const canApproveLeaves = permissions.has("leaves.approve") || permissions.has("leaves.read_all");
  const canSeeWUs = permissions.has("workunits.read");
  const canSeeNotifs = permissions.has("notifications.read_self");
  const canReviewReg = permissions.has("attendance.regularise_review");
  const canSeeProjects = permissions.has("projects.read");
  const canSeeClientDocs = permissions.has("clientdocs.list.global");

  const approvalsQ = useApprovals("pending");
  const leavesPendingQ = useLeaves({ scope: "team", status: "pending" });
  const pendingWUsCountQ = usePendingWorkUnitsCount(canSeeWUs);
  const unreadQ = useUnreadCount();
  const regPendingQ = useRegularisations({ scope: "all", status: "pending" });
  // Pending WU requests visible to actor — backend selector filters by
  // requester (own queue) OR dept-head scope OR SA/Ops global.
  const wuRequestsQ = useWorkUnitRequests(
    { status: "pending" },
    { enabled: canSeeProjects },
  );
  // WU deactivation requests visible to actor — backend selector scopes by
  // requester (own) OR routed dept head OR global tier. Roll into the same
  // Approvals badge so the sidebar surfaces pending action items consistently.
  const wuDeactivationRequestsQ = useDeactivationRequests({ status: "pending" });
  const clientDocsCountsQ = useClientDocsCounts(canSeeClientDocs);

  const approvalsCount =
    (canResolveApprovals ? approvalsQ.data?.results.length ?? 0 : 0) +
    (canSeeProjects ? wuRequestsQ.data?.results.length ?? 0 : 0) +
    (canSeeWUs ? wuDeactivationRequestsQ.data?.results.length ?? 0 : 0);
  const counts: Record<string, number | undefined> = {
    "/approvals": canResolveApprovals || canSeeProjects || canSeeWUs ? approvalsCount : undefined,
    "/leaves": canApproveLeaves ? leavesPendingQ.data?.results.length : undefined,
    "/work-units": canSeeWUs ? pendingWUsCountQ.data?.count : undefined,
    "/notifications": canSeeNotifs ? unreadQ.data?.count : undefined,
    "/regularisations": canReviewReg ? regPendingQ.data?.results.length : undefined,
    "/client-docs": canSeeClientDocs ? clientDocsCountsQ.data?.actionable : undefined,
  };

  const visible = (item: NavItem) => {
    if (item.permission) {
      const required = Array.isArray(item.permission) ? item.permission : [item.permission];
      if (!required.some((p) => permissions.has(p))) return false;
    }
    if (item.hideForRoles && role && item.hideForRoles.includes(role)) return false;
    return true;
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest("a, button")) return;
    toggleSidebar();
  };

  return (
    <aside
      className={cn("sidebar", collapsed && "is-collapsed", mobileOpen && "is-mobile-open")}
      aria-label="Primary navigation"
      onClick={handleBackgroundClick}
      style={{ cursor: "pointer" }}
    >
      <div className={cn("side-strip", stripClass)}>
        <div className="side-strip-dot" aria-hidden />
        {!collapsed && <span>{ROLE_LABEL[role] ?? "USER"}</span>}
      </div>

      <nav className="side-nav">
        {NAV.map((grp) => {
          const items = grp.items.filter(visible);
          if (items.length === 0) return null;
          return (
            <div key={grp.group} className="side-group">
              {!collapsed && <div className="side-group-label">{grp.group}</div>}
              {items.map((it) => {
                const Icon = it.icon;
                const count = counts[it.to];
                const showBadge = typeof count === "number" && count > 0;
                const isUrgent = it.to === "/approvals" && showBadge;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={closeMobileSidebar}
                    className={({ isActive }) =>
                      cn("side-item", isActive && "is-active", isUrgent && "is-urgent")
                    }
                    style={
                      isUrgent
                        ? {
                            color: "var(--danger-700)",
                            fontWeight: 700,
                            background: "var(--danger-50)",
                            borderLeft: "3px solid var(--danger-600)",
                          }
                        : undefined
                    }
                  >
                    <span className="side-item-bar" aria-hidden />
                    <span style={{ position: "relative", display: "inline-flex" }}>
                      <Icon size={18} />
                      {collapsed && showBadge && (
                        <span
                          style={{
                            position: "absolute",
                            top: -4,
                            right: -6,
                            minWidth: 14,
                            height: 14,
                            padding: "0 3px",
                            borderRadius: 999,
                            background: "var(--danger-600)",
                            color: "#fff",
                            fontSize: 9,
                            fontWeight: 700,
                            lineHeight: "14px",
                            textAlign: "center",
                            boxShadow: isUrgent ? "0 0 0 2px var(--danger-200)" : undefined,
                          }}
                        >
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </span>
                    {!collapsed && <span className="side-item-label">{it.label}</span>}
                    {!collapsed && showBadge && (
                      <span
                        style={{
                          marginLeft: "auto",
                          minWidth: isUrgent ? 22 : 18,
                          height: isUrgent ? 22 : 18,
                          padding: isUrgent ? "0 8px" : "0 6px",
                          borderRadius: 999,
                          background: "var(--danger-600)",
                          color: "#fff",
                          fontSize: isUrgent ? 11 : 10,
                          fontWeight: 700,
                          lineHeight: isUrgent ? "22px" : "18px",
                          textAlign: "center",
                          boxShadow: isUrgent ? "0 0 0 3px var(--danger-200)" : undefined,
                        }}
                      >
                        {count > 99 ? "99+" : count}
                      </span>
                    )}
                    {collapsed && (
                      <span className="side-tip" role="tooltip">
                        {it.label}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="side-foot">
        <button
          type="button"
          onClick={onSignOut}
          disabled={logout.isPending}
          title="Sign out"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 8,
            width: "100%",
            padding: collapsed ? "8px" : "8px 10px",
            marginBottom: 10,
            borderRadius: "var(--r-md)",
            border: "1px solid var(--border)",
            background: "transparent",
            color: "var(--danger-700)",
            fontSize: 12,
            fontWeight: 500,
            cursor: logout.isPending ? "not-allowed" : "pointer",
          }}
        >
          <LogOut size={14} aria-hidden />
          {!collapsed && <span>{logout.isPending ? "Signing out…" : "Sign out"}</span>}
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between" }}>
          {!collapsed && (
            <div className="side-build">
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-700)" }}>Workman</div>
              <div style={{ fontSize: 11, color: "var(--gray-500)" }}>
                {import.meta.env.VITE_VERSION}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "var(--r-md)",
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--gray-500)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
          </button>
        </div>
      </div>
      <LogoutAttendancePrompt
        open={logoutPromptOpen}
        onCancel={() => setLogoutPromptOpen(false)}
        onSignOutOnly={() => {
          setLogoutPromptOpen(false);
          void finalizeLogout();
        }}
      />
    </aside>
  );
}